import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, Loader2, User } from "lucide-react";
import PageHead from "@/components/PageHead";
import { MatchRing } from "@/components/ui-bits";
import { api } from "@/lib/api";

const STATUS = ["All", "New", "Screening", "Interview"];

export default function CandidateSearch() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchCandidates = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/recruiter/candidates", {
          params: { q },
        });
        if (active) {
          setCandidates(data?.candidates || []);
        }
      } catch (_) {
        if (active) setCandidates([]);
      } finally {
        if (active) setLoading(false);
      }
    };

    const timer = setTimeout(fetchCandidates, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [q]);

  const filtered = useMemo(
    () =>
      candidates.filter((c) => {
        const ss = status === "All" || c.status === status;
        const qq =
          !q ||
          (c.name + c.role + c.location + (c.skills || []).join(" "))
            .toLowerCase()
            .includes(q.toLowerCase());
        return ss && qq;
      }),
    [candidates, q, status]
  );

  return (
    <div>
      <PageHead
        label="Candidate Search"
        title="Find talent that fits"
        sub="Verified candidates from registered Career Profiles matched by role and skills."
      />

      <div className="mb-5 flex flex-col gap-3 sm:flex-row">
        <div className="flex flex-1 items-center gap-2 rounded-full border border-border bg-card px-4 shadow-xs">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            data-testid="candidate-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, role or skill…"
            className="w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
          />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-electric" />}
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {STATUS.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
              status === s
                ? "border-violet bg-violet text-white shadow-xs"
                : "border-border hover:bg-secondary"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex min-h-[220px] items-center justify-center rounded-3xl border border-dashed border-border">
          <Loader2 className="h-6 w-6 animate-spin text-violet" />
          <span className="ml-2 text-xs text-muted-foreground">Searching candidates…</span>
        </div>
      ) : filtered.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2">
          {filtered.map((c) => (
            <motion.div
              key={c.id}
              whileHover={{ y: -3 }}
              className="card-surface p-5 shadow-sm"
              data-testid={`candidate-${c.id}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-electric to-violet font-heading text-xl font-bold text-white shadow-xs">
                  {(c.name || "C").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="font-heading text-base font-bold truncate">{c.name}</p>
                    <MatchRing value={c.match || 88} size={44} stroke={5} color="hsl(var(--chart-4))" />
                  </div>
                  <p className="text-xs text-muted-foreground">{c.role} · {c.exp}</p>
                  <p className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                    <MapPin className="h-3.5 w-3.5 text-electric" />
                    {c.location}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(c.skills || []).map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-muted-foreground"
                  >
                    {s}
                  </span>
                ))}
              </div>
              <Link
                to={`/recruiter/candidates/${c.id}`}
                className="cv-btn-primary mt-4 w-full py-2.5 text-xs font-semibold text-center block"
              >
                View Profile
              </Link>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="py-12 text-center text-muted-foreground rounded-3xl border border-dashed border-border p-6">
          <p className="font-heading text-base font-semibold">No candidates found</p>
          <p className="text-xs mt-1">Try clearing filters or searching for different keywords.</p>
        </div>
      )}
    </div>
  );
}
